/* eslint-disable react-refresh/only-export-components -- come ActiveMatchContext: il provider e il suo hook vivono nello
   stesso modulo perche' sono un'unita' sola. */
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { ConfermaLasciaTavolo } from '@/components/layout/ConfermaLasciaTavolo';
import { useActiveMatchOpzionale } from '@/contexts/ActiveMatchContext';
import { eIlTavoloScacchi } from '@/lib/chess/tavolo';
import { ascoltaTavoloScacchi, tavoloScacchiAperto } from '@/lib/chess/tavoloAperto';
import { eIlTavolo, uscitaCostaOrologio } from '@/lib/qgammon/lasciareIlTavolo';
import { impostaFrenoIndietro } from '@/lib/livelliIndietro';

/**
 * LA CONFERMA «LASCIARE IL TAVOLO?» — un proprietario solo, per la barra E per il tasto indietro di Telegram.
 *
 * [Luca, I-392 «4 (b)» per la barra; I-402 (~00:40Z) per l'indietro, sul consiglio (b) di tma-orcb-32-barra] Dal tavolo di una
 * partita di BACKGAMMON, col MIO orologio che corre in una partita con orologio, lasciare il tavolo costa tempo e la pastiglia
 * che riporta dentro non dice quanto ne resta. Il quando e' UN predicato (`lib/qgammon/lasciareIlTavolo.ts`), il foglio e'
 * UNO (`ConfermaLasciaTavolo`), e questo provider li tiene insieme: chi sta per lasciare il tavolo gli passa il PASSO che
 * farebbe (`navigate(to)` per la barra, il passo indietro per il BackButton) e gli chiede «lo chiedo prima?».
 *  · `true`  → il foglio e' aperto e «Esci» eseguira' quel passo; chi chiede NON deve fare niente;
 *  · `false` → nessuna conferma: si fa il passo subito, come sempre.
 *
 * Perche' un provider e non lo stato dentro `TabBar` (come in #602): l'indietro vive in `useBackButton`, chiamato in `AppShell`
 * FUORI da `ActiveMatchProvider`, e non puo' leggere la partita ne' aprire un foglio. Il provider sta DENTRO
 * `ActiveMatchProvider`, e per l'indietro si registra come FRENO (`impostaFrenoIndietro`, `lib/livelliIndietro.ts`).
 * ⚠️ Un foglio gia' aperto + indietro: lo chiude la pila dei livelli (`Sheet` si registra), come «Resta»: nessuna seconda
 * conferma. ⚠️ Fuori dal provider (i banchi che montano la `TabBar` da sola) il valore e' `() => false`.
 * 🔴 ESTESA AGLI SCACCHI (E6 di ADR-0193, P4a; reference 07/10 punto 8): il tavolo degli scacchi si registra in
 * `lib/chess/tavoloAperto.ts` (il suo socket sta nella rotta, non in `ActiveMatchContext`) e il predicato e' di
 * `uscitaCostaOrologio` degli scacchi (`lib/chess/tavolo.ts`: il MIO orologio corre — con R10 la frase e' VERA anche a
 * connessione persa). Il foglio e' lo STESSO `ConfermaLasciaTavolo`, con le stesse parole; le ancore valgono come per il
 * backgammon e portano il GIOCO, cosi' un tavolo di un gioco non puo' chiudere la domanda di un altro.
 * ⚠️ Lo swipe-back di iOS: se Telegram lo manda come click del BackButton passa di qui; se lo gestisce lui, l'app non lo vede
 * (l'app disabilita solo lo swipe verticale, `lib/telegram.ts`, e non ascolta `popstate`). NON provato.
 */
type ChiediPrimaDiUscire = (passo: () => void) => boolean;

const UscitaTavoloContext = createContext<ChiediPrimaDiUscire>(() => false);

/** `(passo) => boolean`: vero se la conferma ha preso in carico il passo (v. sopra). */
export function useChiediPrimaDiUscire(): ChiediPrimaDiUscire {
  return useContext(UscitaTavoloContext);
}

export function UscitaTavoloProvider({ children }: { children: ReactNode }) {
  const partita = useActiveMatchOpzionale();
  const { pathname } = useLocation();
  // La domanda aperta: il passo da eseguire su «Esci» (una funzione dentro lo stato: si imposta con `() => passo`) e le DUE
  // ANCORE del momento in cui e' stata fatta, la partita e il percorso (ADR-0197 §2).
  const [inAttesa, setInAttesa] = useState<{ gioco: 'qgammon' | 'chess'; passo: () => void; matchId: string; pathname: string } | null>(null);
  // Il tavolo degli scacchi aperto (se c'e'): il registro e' esterno, e la rotta degli scacchi lo tiene finche' e' montata.
  const scacchi = useSyncExternalStore(ascoltaTavoloScacchi, tavoloScacchiAperto);

  const chiedi: ChiediPrimaDiUscire = (passo) => {
    if (scacchi !== null && eIlTavoloScacchi(pathname, scacchi.matchId)) {
      if (scacchi.finita || !scacchi.costaOrologio()) return false;
      setInAttesa({ gioco: 'chess', passo, matchId: scacchi.matchId, pathname });
      return true;
    }
    const d = partita?.descriptor;
    if (!partita || !d || d.game !== 'qgammon') return false;
    if (!eIlTavolo(pathname, d.matchId)) return false;
    if (!uscitaCostaOrologio(partita.qgammon.state, d.perspective)) return false;
    setInAttesa({ gioco: 'qgammon', passo, matchId: d.matchId, pathname });
    return true;
  };

  /**
   * 🔴 LA DOMANDA SCADE CON LE SUE ANCORE (ADR-0197 §2; difetto trovato da tma-orcb sul gate). Se, a foglio aperto, la partita
   * finisce o viene tolta (il descrittore diventa `null`, o non e' piu' qgammon, o e' un'altra partita) o si lascia la rotta
   * del tavolo per un'altra strada, la domanda si ANNULLA, SENZA navigare. Senza questo `inAttesa` restava impostato dopo che
   * il foglio si era smontato, e alla partita di backgammon SUCCESSIVA il foglio si rimontava gia' aperto: una conferma che
   * nessuno aveva chiesto, con un passo vecchio dentro.
   * ⚠️ Cio' che NON la annulla: il cambio di TURNO (o dell'orologio). La scelta e' dell'utente, e «Esci» esegue il passo
   * senza rivalutare il predicato.
   * Lo `aperto` e' DERIVATO dalle ancore (non solo dallo stato): fra la fine della partita e l'effetto che azzera non c'e' un
   * frame con il foglio aperto sulla domanda sbagliata.
   */
  const d = partita?.descriptor;
  const ancoreVive =
    inAttesa !== null &&
    pathname === inAttesa.pathname &&
    (inAttesa.gioco === 'chess'
      ? scacchi !== null && scacchi.matchId === inAttesa.matchId && !scacchi.finita
      : d?.game === 'qgammon' && d.matchId === inAttesa.matchId);
  useEffect(() => {
    if (inAttesa !== null && !ancoreVive) setInAttesa(null);
  }, [inAttesa, ancoreVive]);
  // Il freno dell'indietro e' registrato UNA volta e legge sempre l'ultima `chiedi` (partita e percorso del momento del tocco).
  const ultima = useRef(chiedi);
  useLayoutEffect(() => {
    ultima.current = chiedi;
  });
  const stabile = useCallback<ChiediPrimaDiUscire>((passo) => ultima.current(passo), []);
  useEffect(() => impostaFrenoIndietro(stabile), [stabile]);

  return (
    <UscitaTavoloContext.Provider value={stabile}>
      {children}
      {/* Montato solo con una partita di backgammon viva: senza, non esiste (e non porta con se' gli hook del foglio). Resta
          montato a foglio chiuso per l'animazione d'uscita di `Sheet`. */}
      {(partita?.descriptor?.game === 'qgammon' || scacchi !== null) && (
        <ConfermaLasciaTavolo
          open={ancoreVive}
          onResta={() => setInAttesa(null)}
          onEsci={() => {
            const passo = inAttesa?.passo;
            setInAttesa(null);
            passo?.();
          }}
        />
      )}
    </UscitaTavoloContext.Provider>
  );
}
